import React, { useState } from "react";

/**
 * CSP Nonce & Security Guide for Velaro widget deployments.
 *
 * HOW NONCE WORKS WITH THE VELARO WIDGET:
 * ────────────────────────────────────────
 * 1. Customer's server generates a cryptographic nonce per page request.
 * 2. The nonce is added to the Velaro <script nonce="..."> tag AND the
 *    Content-Security-Policy header: script-src 'nonce-<value>' 'strict-dynamic'.
 * 3. The browser trusts the inline script because the nonce matches.
 * 4. 'strict-dynamic' automatically trusts any script dynamically created
 *    by the already-trusted script (embed.js, messenger bundle, etc.).
 * 5. No CDN domain allowlisting needed in script-src — strict-dynamic handles it.
 *
 * This is PURELY ADDITIVE — customers who don't use CSP are unaffected.
 * The standard embed code works exactly as before with no nonce.
 */

// ── Reusable helpers ─────────────────────────────────────────────────────────

function SysPill({ children }: { children: React.ReactNode }) {
  return (
    <code className="text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded font-mono text-xs break-words">
      {children}
    </code>
  );
}

function CopyBlock({ text, id, copied, onCopy }: {
  text: string;
  id: string;
  copied: string | null;
  onCopy: (t: string, k: string) => void;
}) {
  return (
    <div className="relative">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed">
        {text}
      </pre>
      <button
        type="button"
        className="absolute top-2 right-2 text-xs bg-white/10 border border-white/20 text-white rounded px-2 py-1 hover:bg-white/20 transition-colors"
        onClick={() => onCopy(text, id)}
        aria-label={`Copy ${id}`}
      >
        {copied === id ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

// ── Types ────────────────────────────────────────────────────────────────────

interface Props {
  /** The standard embed code (without nonce) — used to generate the nonce variant */
  embedScript: string;
  /** "messenger" or "widget" — affects connect-src domains shown */
  variant: "messenger" | "widget";
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function DeploymentSecurityGuide({ embedScript, variant }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  }

  // Generate nonce version of the embed script.
  // For messenger: single <script> tag — just add nonce attribute.
  // For widget: two <script> tags — combine into one inline + dynamic load.
  const nonceScript = generateNonceScript(embedScript, variant);

  return (
    <div className="space-y-4">

      {/* ── CSP Nonce (primary method) ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white flex items-center gap-2">
          <i className="fa-solid fa-shield-halved text-green-600 text-xs" />
          Content Security Policy (CSP) with Nonce
          <span className="ml-auto text-xs text-green-600 bg-green-50 border border-green-200 rounded px-2 py-0.5">Recommended</span>
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-4">

          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            <strong>No changes required if you don't use CSP.</strong> This section is only
            relevant if your website enforces a Content-Security-Policy header. Existing
            deployments without CSP continue to work exactly as before.
          </div>

          <div className="text-sm text-slate-600 space-y-2">
            <p>
              If your site uses CSP, add a <SysPill>nonce</SysPill> attribute to the
              Velaro embed script and include <SysPill>strict-dynamic</SysPill> in your
              policy. This creates a trust chain: the browser trusts the inline script
              (via nonce), and <SysPill>strict-dynamic</SysPill> automatically trusts
              every script it loads dynamically — no CDN allowlisting needed.
            </p>
            <p>
              The Velaro widget does <strong>not</strong> use <SysPill>eval()</SysPill> or{" "}
              <SysPill>new Function()</SysPill>, so <SysPill>unsafe-eval</SysPill> is{" "}
              <strong>never required</strong>.
            </p>
          </div>

          {/* Step 1: Nonce embed code */}
          <div>
            <div className="text-sm font-medium text-slate-700 mb-1">
              Step 1 — Add <SysPill>nonce</SysPill> to your embed script
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Replace <SysPill>YOUR_SERVER_GENERATED_NONCE</SysPill> with the nonce
              your server generates per request. Use the same nonce you use for your
              other CSP-protected scripts.
            </p>
            <CopyBlock id="csp-nonce-embed" text={nonceScript} copied={copied} onCopy={copy} />
          </div>

          {/* Step 2: CSP header */}
          <div>
            <div className="text-sm font-medium text-slate-700 mb-1">
              Step 2 — Set the CSP header (or meta tag)
            </div>
            <CopyBlock id="csp-header" text={CSP_HEADER_TEMPLATE} copied={copied} onCopy={copy} />
          </div>

          {/* Directive reference table */}
          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              CSP directive reference
            </summary>
            <div className="px-4 pb-4 pt-2">
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <span>Directive</span>
                  <span>Required values</span>
                  <span>Purpose</span>
                </div>
                {CSP_DIRECTIVES.map(({ directive, values, purpose }, i) => (
                  <div key={i} className={`grid grid-cols-3 px-4 py-2.5 items-start text-xs gap-2 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                    <SysPill>{directive}</SysPill>
                    <span className="text-slate-600 font-mono text-xs break-all">{values}</span>
                    <span className="text-slate-500">{purpose}</span>
                  </div>
                ))}
              </div>

              {/* Optional directives */}
              <div className="mt-3 text-xs text-slate-500">
                <strong>Optional directives</strong> (add if you use these features):
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden mt-2">
                <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <span>Feature</span>
                  <span>Directive</span>
                  <span>Value</span>
                </div>
                {OPTIONAL_DIRECTIVES.map(({ feature, directive, value }, i) => (
                  <div key={i} className={`grid grid-cols-3 px-4 py-2.5 items-start text-xs gap-2 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                    <span className="text-slate-600">{feature}</span>
                    <SysPill>{directive}</SysPill>
                    <span className="text-slate-500 font-mono text-xs">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </details>

          {/* Server-side nonce generation examples */}
          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              Server-side nonce generation examples (8 platforms)
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-4">
              <p className="text-xs text-slate-500">
                The nonce must be a <strong>cryptographically random</strong> string, generated
                fresh on <strong>every page request</strong>. A static or hardcoded nonce defeats
                the security purpose. Use 32 bytes (256 bits) minimum, base64-encoded.
              </p>

              {NONCE_EXAMPLES.map(({ platform, language, code }, i) => (
                <details key={i} className="border border-slate-200 rounded-lg">
                  <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
                    {platform}
                    <span className="text-xs text-slate-400 ml-2">({language})</span>
                  </summary>
                  <div className="px-4 pb-4 pt-1">
                    <CopyBlock id={`nonce-${platform.toLowerCase().replace(/\W/g, "")}`} text={code} copied={copied} onCopy={copy} />
                  </div>
                </details>
              ))}
            </div>
          </details>

          {/* Hosted platforms */}
          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              Hosted platforms (WordPress, Webflow, Wix, Squarespace)
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-3 text-sm text-slate-600">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-xs">
                <strong>Note:</strong> Most sites on hosted platforms do not use CSP. If you
                don't have a CSP header, the standard embed code works as-is — no nonce needed.
              </div>
              <div>
                <strong>WordPress:</strong> Use a CSP plugin such as{" "}
                <em>CSP Nonce</em>, <em>HTTP Headers</em>, or <em>Really Simple Security</em>.
                These handle nonce generation automatically and apply nonces to inline scripts.
                Set up the CSP directives from the reference table above.
              </div>
              <div>
                <strong>Webflow / Squarespace / Wix:</strong> These platforms don't support
                server-side nonce generation. If you need CSP, use a reverse proxy
                (Cloudflare Workers, AWS CloudFront Functions, or Fastly) to inject CSP
                headers and nonces. See the Cloudflare Workers example in the generation
                examples above.
              </div>
              <div>
                <strong>Google Tag Manager:</strong> GTM-injected scripts are dynamically
                created, so they benefit from <SysPill>strict-dynamic</SysPill> if the
                GTM container script itself has a nonce. Add{" "}
                <SysPill>nonce="YOUR_NONCE"</SysPill> to your GTM container{" "}
                <SysPill>&lt;script&gt;</SysPill> tag. All tags fired by GTM (including
                Velaro) will then be trusted automatically.
              </div>
            </div>
          </details>

          {/* Troubleshooting */}
          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              Testing and troubleshooting
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-3">
              <div className="text-sm text-slate-600">
                <strong>To verify:</strong> Open Developer Tools (F12) &rarr; Console. If
                the widget loads without red CSP errors, your policy is correct.
              </div>
              <div className="text-xs text-slate-500 mb-2">
                <strong>Tip:</strong> Use <SysPill>Content-Security-Policy-Report-Only</SysPill>{" "}
                instead of <SysPill>Content-Security-Policy</SysPill> during testing. This logs
                violations without blocking anything, so you can verify before enforcing.
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-2 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <span>Error message</span>
                  <span>Fix</span>
                </div>
                {CSP_ERRORS.map(({ error, fix }, i) => (
                  <div key={i} className={`grid grid-cols-2 px-4 py-2.5 items-start text-xs gap-2 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                    <span className="text-red-600 font-mono break-words">{error}</span>
                    <span className="text-slate-600">{fix}</span>
                  </div>
                ))}
              </div>
            </div>
          </details>

        </div>
      </details>

      {/* ── Alternative: CSP Hash-based (no server-side nonce needed) ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white flex items-center gap-2">
          <i className="fa-solid fa-hashtag text-blue-600 text-xs" />
          Alternative: CSP with Hash (no server-side nonce generation)
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <div className="text-sm text-slate-600">
            If you can't generate a nonce server-side (static sites, CDN-only hosting),
            you can use a <strong>hash-based</strong> CSP instead. The browser computes a
            SHA-256 hash of the inline script and checks it against the hash in the CSP header.
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-2">
            <p>
              <strong>Trade-off:</strong> Hash-based CSP is less flexible than nonce.
              If the embed script content changes (e.g. you update the deployment ID),
              the hash changes and you must update your CSP header. Nonce doesn't have
              this problem because it's per-request, not per-content.
            </p>
            <p>
              <strong>When to use:</strong> Static sites (GitHub Pages, Netlify, S3),
              CDN-served pages, or any environment where you control HTTP headers but
              not server-side rendering.
            </p>
          </div>

          <div className="text-sm font-medium text-slate-700 mb-1">
            How to generate the hash
          </div>
          <CopyBlock id="csp-hash-gen" text={HASH_GENERATION_SNIPPET} copied={copied} onCopy={copy} />

          <div className="text-sm font-medium text-slate-700 mb-1">
            Then use the hash in your CSP header
          </div>
          <CopyBlock id="csp-hash-header" text={HASH_CSP_HEADER} copied={copied} onCopy={copy} />

          <div className="text-xs text-slate-500">
            <strong>Note:</strong> The hash must match the <em>exact</em> content between
            the <SysPill>&lt;script&gt;</SysPill> tags — including whitespace and newlines.
            Any change to the script content invalidates the hash. Combine with{" "}
            <SysPill>strict-dynamic</SysPill> so dynamically loaded scripts (embed.js,
            messenger bundle) are automatically trusted.
          </div>
        </div>
      </details>

      {/* ── Subresource Integrity (SRI) ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white flex items-center gap-2">
          <i className="fa-solid fa-fingerprint text-purple-600 text-xs" />
          Subresource Integrity (SRI)
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <div className="text-sm text-slate-600">
            SRI lets you verify that external scripts haven't been tampered with
            by adding a cryptographic hash. The browser checks the downloaded file
            against the hash before executing it.
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <strong>Important:</strong> SRI is best suited for scripts with fixed content
            (versioned CDN files). Because the Velaro widget is continuously updated to add
            features and fixes, the hash would change frequently. If you pin to a hash,
            you'll need to update it when the widget updates. For most deployments,{" "}
            <strong>CSP with nonce + strict-dynamic is the better choice</strong> because it
            doesn't require hash updates.
          </div>
          <div className="text-sm font-medium text-slate-700 mb-1">
            How to generate and use an SRI hash
          </div>
          <CopyBlock id="sri-snippet" text={SRI_SNIPPET} copied={copied} onCopy={copy} />
          <div className="text-xs text-slate-500">
            <SysPill>crossorigin="anonymous"</SysPill> is required for SRI on cross-origin
            scripts. The <SysPill>integrity</SysPill> attribute accepts{" "}
            <SysPill>sha256</SysPill>, <SysPill>sha384</SysPill>, or{" "}
            <SysPill>sha512</SysPill>.
          </div>
        </div>
      </details>

      {/* ── Trusted Types ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white flex items-center gap-2">
          <i className="fa-solid fa-lock text-indigo-600 text-xs" />
          Trusted Types (advanced DOM XSS prevention)
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <div className="text-sm text-slate-600">
            Trusted Types is a newer browser API (Chrome 83+, Edge 83+) that prevents
            DOM-based XSS by requiring that dangerous sinks (like{" "}
            <SysPill>innerHTML</SysPill>, <SysPill>document.write</SysPill>) only
            accept typed objects instead of raw strings.
          </div>
          <div className="text-sm text-slate-600">
            The Velaro widget is <strong>Trusted Types compatible</strong> — it does not
            use <SysPill>innerHTML</SysPill>, <SysPill>document.write</SysPill>,
            or <SysPill>eval()</SysPill>. If you enforce Trusted Types on your site,
            the widget will work without a policy exception.
          </div>
          <div className="text-sm font-medium text-slate-700 mb-1">
            To enable Trusted Types alongside your CSP
          </div>
          <CopyBlock id="trusted-types" text={TRUSTED_TYPES_SNIPPET} copied={copied} onCopy={copy} />
          <div className="text-xs text-slate-500">
            <strong>Browser support:</strong> Chromium-based browsers (Chrome, Edge, Opera,
            Brave). Firefox and Safari have it behind flags. Use alongside CSP nonce for
            defense-in-depth. Trusted Types and CSP nonce complement each other — CSP
            controls <em>which</em> scripts run, Trusted Types controls <em>what</em>{" "}
            scripts can do to the DOM.
          </div>
        </div>
      </details>

      {/* ── Permissions Policy ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white flex items-center gap-2">
          <i className="fa-solid fa-sliders text-teal-600 text-xs" />
          Permissions Policy (restrict browser features)
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <div className="text-sm text-slate-600">
            Permissions Policy (formerly Feature Policy) lets you control which browser
            features (camera, microphone, geolocation) can be used on your page and by
            embedded iframes. If you use Video Chat, make sure camera/microphone are allowed.
          </div>
          <CopyBlock id="permissions-policy" text={PERMISSIONS_POLICY_SNIPPET} copied={copied} onCopy={copy} />
          <div className="text-xs text-slate-500">
            Adjust based on which Velaro features you use. If you don't use Video Chat,
            you can omit <SysPill>camera</SysPill> and <SysPill>microphone</SysPill>.
          </div>
        </div>
      </details>

      {/* ── Quick reference card ── */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 space-y-2">
        <div className="font-semibold text-sm text-slate-700">Security method comparison</div>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-5 bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 gap-2">
            <span>Method</span>
            <span>Protects against</span>
            <span>Server needed?</span>
            <span>Maintenance</span>
            <span>Best for</span>
          </div>
          {COMPARISON_TABLE.map(({ method, protects, server, maintenance, best }, i) => (
            <div key={i} className={`grid grid-cols-5 px-3 py-2 items-start text-xs gap-2 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
              <span className="font-medium text-slate-700">{method}</span>
              <span className="text-slate-600">{protects}</span>
              <span className="text-slate-500">{server}</span>
              <span className="text-slate-500">{maintenance}</span>
              <span className="text-slate-500">{best}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// ── Script generation helpers ────────────────────────────────────────────────

function generateNonceScript(embedScript: string, variant: "messenger" | "widget"): string {
  if (variant === "widget") {
    // Widget has two script tags. For CSP nonce, combine into one inline
    // script that dynamically loads the external one — one nonce covers all.
    //
    // Parse out siteId, widgetId, and scriptSrc from the raw widget embed.
    const siteIdMatch = embedScript.match(/siteId:\s*"([^"]+)"/);
    const widgetIdMatch = embedScript.match(/widgetId:\s*"([^"]+)"/);
    const srcMatch = embedScript.match(/src="([^"]+)"/);

    const siteId = siteIdMatch?.[1] ?? "YOUR_SITE_ID";
    const widgetId = widgetIdMatch?.[1] ?? "YOUR_WIDGET_ID";
    const scriptSrc = srcMatch?.[1] ?? "https://messaging.velaro.com/widget.js";

    return [
      `<!-- Velaro Page Widget — CSP nonce version -->`,
      `<!-- Replace YOUR_SERVER_GENERATED_NONCE with a fresh nonce per request -->`,
      `<script nonce="YOUR_SERVER_GENERATED_NONCE">`,
      `  window.__velaro_pw = { siteId: "${siteId}", widgetId: "${widgetId}" };`,
      `  (function() {`,
      `    var s = document.createElement("script");`,
      `    s.src = "${scriptSrc}";`,
      `    s.async = true;`,
      `    document.head.appendChild(s);`,
      `  })();`,
      `</script>`,
    ].join("\n");
  }

  // Messenger: just add nonce attribute to the existing script tag
  return embedScript
    .replace(
      "<script>",
      `<!-- Replace YOUR_SERVER_GENERATED_NONCE with a fresh nonce per request -->\n<script nonce="YOUR_SERVER_GENERATED_NONCE">`
    );
}

// ── Constants ────────────────────────────────────────────────────────────────

const CSP_HEADER_TEMPLATE = `Content-Security-Policy:
  script-src 'nonce-YOUR_NONCE' 'strict-dynamic';
  style-src  'self' 'unsafe-inline' https://fonts.googleapis.com;
  font-src   'self' https://fonts.gstatic.com;
  connect-src 'self' https://*.velaro.com wss://*.velaro.com
              https://*.azurewebsites.net https://*.service.signalr.net;
  img-src    'self' data: https:;
  frame-src  'self' blob:;
  object-src 'none';
  base-uri   'self';`;

const CSP_DIRECTIVES = [
  {
    directive: "script-src",
    values: "'nonce-<value>' 'strict-dynamic'",
    purpose: "Trusts the Velaro boot script (nonce) and everything it loads dynamically (strict-dynamic)"
  },
  {
    directive: "style-src",
    values: "'self' 'unsafe-inline' fonts.googleapis.com",
    purpose: "Widget applies inline styles for positioning/theming. Google Fonts for widget fonts."
  },
  {
    directive: "font-src",
    values: "'self' fonts.gstatic.com",
    purpose: "Google Font files (Open Sans, Roboto, etc.) if configured in your widget theme"
  },
  {
    directive: "connect-src",
    values: "*.velaro.com wss://*.velaro.com *.azurewebsites.net *.service.signalr.net",
    purpose: "API calls, WebSocket (SignalR real-time messaging), Azure services"
  },
  {
    directive: "img-src",
    values: "'self' data: https:",
    purpose: "Agent avatars, company logos, inline SVG icons, uploaded images"
  },
  {
    directive: "frame-src",
    values: "'self' blob:",
    purpose: "Widget renders in an iframe for DOM isolation"
  },
  {
    directive: "object-src",
    values: "'none'",
    purpose: "Blocks Flash/Java plugins (security best practice)"
  },
  {
    directive: "base-uri",
    values: "'self'",
    purpose: "Prevents base tag injection attacks"
  },
];

const OPTIONAL_DIRECTIVES = [
  { feature: "Notification sounds", directive: "media-src", value: "https://*.velaro.com" },
  { feature: "Video Chat", directive: "connect-src", value: "(add) turn:YOUR_TURN_SERVER:3478" },
  { feature: "File uploads", directive: "connect-src", value: "(already covered by *.velaro.com)" },
  { feature: "Knowledge Base links", directive: "frame-src", value: "(add) https://help.velaro.com" },
];

const CSP_ERRORS = [
  {
    error: "Refused to execute inline script...",
    fix: "The nonce on your <script> tag doesn't match the CSP header, or the nonce is missing. Verify the nonce value is identical in both places."
  },
  {
    error: "Refused to load the script '...embed.js'...",
    fix: "Missing 'strict-dynamic' in script-src. Add it so dynamically-created scripts are trusted."
  },
  {
    error: "Refused to connect to 'https://...velaro.com'...",
    fix: "Add https://*.velaro.com and wss://*.velaro.com to connect-src."
  },
  {
    error: "Refused to connect to 'wss://...'...",
    fix: "Add wss://*.velaro.com to connect-src for WebSocket/SignalR."
  },
  {
    error: "Refused to load the font 'https://fonts.gstatic.com/...'",
    fix: "Add https://fonts.gstatic.com to font-src."
  },
  {
    error: "Refused to load the stylesheet 'https://fonts.googleapis.com/...'",
    fix: "Add https://fonts.googleapis.com to style-src."
  },
  {
    error: "Refused to frame '...'",
    fix: "Add blob: to frame-src (the widget uses blob URLs for iframe isolation)."
  },
];

const NONCE_EXAMPLES = [
  {
    platform: "ASP.NET Core",
    language: "C# / Razor",
    code: `// In middleware (Startup.cs or Program.cs):
app.Use(async (context, next) =>
{
    var nonce = Convert.ToBase64String(
        System.Security.Cryptography.RandomNumberGenerator.GetBytes(32));
    context.Items["csp-nonce"] = nonce;

    context.Response.Headers.Append("Content-Security-Policy",
        $"script-src 'nonce-{nonce}' 'strict-dynamic'; " +
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; " +
        "font-src 'self' https://fonts.gstatic.com; " +
        "connect-src 'self' https://*.velaro.com wss://*.velaro.com " +
        "https://*.azurewebsites.net https://*.service.signalr.net; " +
        "img-src 'self' data: https:; frame-src 'self' blob:; " +
        "object-src 'none'; base-uri 'self';");

    await next();
});

// In your Razor view:
// <script nonce="@Context.Items["csp-nonce"]">
//   // Velaro embed code here
// </script>`
  },
  {
    platform: "Node.js / Express",
    language: "JavaScript",
    code: `const crypto = require('crypto');

app.use((req, res, next) => {
  const nonce = crypto.randomBytes(32).toString('base64');
  res.locals.nonce = nonce;

  res.setHeader('Content-Security-Policy', [
    \`script-src 'nonce-\${nonce}' 'strict-dynamic'\`,
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    "font-src 'self' https://fonts.gstatic.com",
    "connect-src 'self' https://*.velaro.com wss://*.velaro.com " +
      "https://*.azurewebsites.net https://*.service.signalr.net",
    "img-src 'self' data: https:",
    "frame-src 'self' blob:",
    "object-src 'none'",
    "base-uri 'self'"
  ].join('; '));
  next();
});

// In EJS: <script nonce="<%= nonce %>">
// In Handlebars: <script nonce="{{nonce}}">
// In Pug: script(nonce=nonce)`
  },
  {
    platform: "Next.js",
    language: "TypeScript",
    code: `// middleware.ts (App Router)
import { NextResponse } from 'next/server';
import type { NextRequest } from 'next/server';

export function middleware(request: NextRequest) {
  const nonce = Buffer.from(crypto.randomUUID()).toString('base64');
  const response = NextResponse.next();

  response.headers.set('Content-Security-Policy', [
    \`script-src 'nonce-\${nonce}' 'strict-dynamic'\`,
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    "font-src 'self' https://fonts.gstatic.com",
    "connect-src 'self' https://*.velaro.com wss://*.velaro.com " +
      "https://*.azurewebsites.net https://*.service.signalr.net",
    "img-src 'self' data: https:",
    "frame-src 'self' blob:",
    "object-src 'none'",
    "base-uri 'self'"
  ].join('; '));

  // Pass nonce to the page via header
  response.headers.set('x-nonce', nonce);
  return response;
}

// In your layout.tsx:
// import { headers } from 'next/headers';
// const nonce = headers().get('x-nonce') ?? '';
// <script nonce={nonce}> ... </script>`
  },
  {
    platform: "PHP",
    language: "PHP",
    code: `<?php
$nonce = base64_encode(random_bytes(32));

header("Content-Security-Policy: " .
  "script-src 'nonce-$nonce' 'strict-dynamic'; " .
  "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; " .
  "font-src 'self' https://fonts.gstatic.com; " .
  "connect-src 'self' https://*.velaro.com wss://*.velaro.com " .
  "https://*.azurewebsites.net https://*.service.signalr.net; " .
  "img-src 'self' data: https:; frame-src 'self' blob:; " .
  "object-src 'none'; base-uri 'self';");
?>

<!-- In your template: -->
<script nonce="<?= htmlspecialchars($nonce, ENT_QUOTES, 'UTF-8') ?>">
  // Velaro embed code here
</script>`
  },
  {
    platform: "Python / Django",
    language: "Python",
    code: `# middleware.py
import secrets, base64

class CSPNonceMiddleware:
    def __init__(self, get_response):
        self.get_response = get_response

    def __call__(self, request):
        nonce = base64.b64encode(secrets.token_bytes(32)).decode('ascii')
        request.csp_nonce = nonce
        response = self.get_response(request)
        response['Content-Security-Policy'] = (
            f"script-src 'nonce-{nonce}' 'strict-dynamic'; "
            "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; "
            "font-src 'self' https://fonts.gstatic.com; "
            "connect-src 'self' https://*.velaro.com wss://*.velaro.com "
            "https://*.azurewebsites.net https://*.service.signalr.net; "
            "img-src 'self' data: https:; frame-src 'self' blob:; "
            "object-src 'none'; base-uri 'self';"
        )
        return response

# In settings.py: MIDDLEWARE = [..., 'yourapp.middleware.CSPNonceMiddleware']
# In template: <script nonce="{{ request.csp_nonce }}">
#   // Velaro embed code
# </script>`
  },
  {
    platform: "Ruby on Rails",
    language: "Ruby",
    code: `# app/controllers/application_controller.rb
class ApplicationController < ActionController::Base
  before_action :set_csp_nonce

  private

  def set_csp_nonce
    @csp_nonce = SecureRandom.base64(32)
    response.set_header('Content-Security-Policy',
      "script-src 'nonce-#{@csp_nonce}' 'strict-dynamic'; " \\
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; " \\
      "font-src 'self' https://fonts.gstatic.com; " \\
      "connect-src 'self' https://*.velaro.com wss://*.velaro.com " \\
      "https://*.azurewebsites.net https://*.service.signalr.net; " \\
      "img-src 'self' data: https:; frame-src 'self' blob:; " \\
      "object-src 'none'; base-uri 'self';")
  end
end

# In ERB: <script nonce="<%= @csp_nonce %>">
#   // Velaro embed code
# </script>`
  },
  {
    platform: "Java / Spring Boot",
    language: "Java",
    code: `// SecurityConfig.java
import org.springframework.security.web.header.writers.*;

@Configuration
@EnableWebSecurity
public class SecurityConfig {
    @Bean
    public SecurityFilterChain filterChain(HttpSecurity http) throws Exception {
        http.headers(headers -> headers
            .contentSecurityPolicy(csp -> csp
                // Spring Security generates the nonce automatically
                // and makes it available via request attribute
                .policyDirectives(
                    "script-src 'nonce-{nonce}' 'strict-dynamic'; " +
                    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; " +
                    "font-src 'self' https://fonts.gstatic.com; " +
                    "connect-src 'self' https://*.velaro.com wss://*.velaro.com " +
                    "https://*.azurewebsites.net https://*.service.signalr.net; " +
                    "img-src 'self' data: https:; frame-src 'self' blob:; " +
                    "object-src 'none'; base-uri 'self';"
                )
            )
        );
        return http.build();
    }
}

// In Thymeleaf:
// <script th:attr="nonce=\${_csrf.token}">
//   // Velaro embed code
// </script>
// Or with Spring's CspNonceFilter:
// <script nonce="\${cspNonce}">
//   // Velaro embed code
// </script>`
  },
  {
    platform: "Cloudflare Workers",
    language: "JavaScript (edge)",
    code: `// For static sites / CDN-hosted pages where you can't
// generate nonces server-side — use Cloudflare Workers
// as a reverse proxy to inject CSP headers and nonces.

export default {
  async fetch(request) {
    const response = await fetch(request);
    const nonce = btoa(String.fromCharCode(
      ...crypto.getRandomValues(new Uint8Array(32))
    ));

    // Rewrite HTML to inject nonce on Velaro script tags
    const rewriter = new HTMLRewriter()
      .on('script[data-velaro-csp]', {
        element(el) { el.setAttribute('nonce', nonce); }
      });

    const newResponse = rewriter.transform(response);
    newResponse.headers.set('Content-Security-Policy', [
      \`script-src 'nonce-\${nonce}' 'strict-dynamic'\`,
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "connect-src 'self' https://*.velaro.com wss://*.velaro.com " +
        "https://*.azurewebsites.net https://*.service.signalr.net",
      "img-src 'self' data: https:",
      "frame-src 'self' blob:",
      "object-src 'none'",
      "base-uri 'self'"
    ].join('; '));

    return newResponse;
  }
};

// In your HTML, mark the Velaro script for nonce injection:
// <script data-velaro-csp>
//   // Velaro embed code — Worker will add nonce=""
// </script>`
  },
];

const HASH_GENERATION_SNIPPET = `# Generate a SHA-256 hash of your embed script content.
# The hash covers ONLY the text between <script> and </script> tags.

# On macOS/Linux:
echo -n 'YOUR_SCRIPT_CONTENT_HERE' | openssl dgst -sha256 -binary | openssl base64

# On Windows (PowerShell):
$bytes = [System.Text.Encoding]::UTF8.GetBytes("YOUR_SCRIPT_CONTENT_HERE")
$hash = [System.Security.Cryptography.SHA256]::Create().ComputeHash($bytes)
[Convert]::ToBase64String($hash)

# Or use: https://report-uri.com/home/hash (paste script content)`;

const HASH_CSP_HEADER = `Content-Security-Policy:
  script-src 'sha256-YOUR_BASE64_HASH_HERE' 'strict-dynamic';
  style-src  'self' 'unsafe-inline' https://fonts.googleapis.com;
  font-src   'self' https://fonts.gstatic.com;
  connect-src 'self' https://*.velaro.com wss://*.velaro.com
              https://*.azurewebsites.net https://*.service.signalr.net;
  img-src    'self' data: https:;
  frame-src  'self' blob:;
  object-src 'none';
  base-uri   'self';`;

const SRI_SNIPPET = `<!-- Generate the SRI hash for the external script -->
<!-- macOS/Linux: -->
<!-- curl -s https://YOUR_MESSAGING_URL/messenger/embed.js | \\
       openssl dgst -sha384 -binary | openssl base64 -->

<!-- Then add the integrity attribute: -->
<script src="https://messaging.velaro.com/messenger/embed.js"
        integrity="sha384-YOUR_HASH_HERE"
        crossorigin="anonymous"
        async></script>

<!-- Note: The hash will change whenever the widget is updated.
     For most deployments, CSP nonce + strict-dynamic is preferred
     because it doesn't require hash updates. -->`;

const TRUSTED_TYPES_SNIPPET = `Content-Security-Policy:
  script-src 'nonce-YOUR_NONCE' 'strict-dynamic';
  require-trusted-types-for 'script';
  trusted-types velaro-policy default;
  /* ... other directives ... */

<!-- The Velaro widget does not inject untrusted strings into
     dangerous sinks, so no Trusted Types policy is needed
     for the widget itself. The "default" policy above is
     for your own application code. -->`;

const PERMISSIONS_POLICY_SNIPPET = `# HTTP header — controls which browser features are allowed.
# Adjust based on which Velaro features you use.

Permissions-Policy:
  camera=(self "https://*.velaro.com"),
  microphone=(self "https://*.velaro.com"),
  geolocation=(),
  payment=(),
  usb=(),
  autoplay=(self "https://*.velaro.com")

# camera + microphone: Required for Video Chat feature.
#   Remove if you don't use Video Chat.
# autoplay: Required for notification sounds.
# geolocation, payment, usb: Blocked (not used by Velaro).`;

const COMPARISON_TABLE = [
  {
    method: "CSP + Nonce",
    protects: "XSS, code injection",
    server: "Yes (generate nonce)",
    maintenance: "None — nonce is per-request",
    best: "Most sites (recommended)"
  },
  {
    method: "CSP + Hash",
    protects: "XSS, code injection",
    server: "No (static hash)",
    maintenance: "Update hash if script changes",
    best: "Static sites, CDN-only"
  },
  {
    method: "SRI",
    protects: "CDN tampering, MITM",
    server: "No",
    maintenance: "Update hash on widget updates",
    best: "High-security / compliance"
  },
  {
    method: "Trusted Types",
    protects: "DOM XSS",
    server: "No",
    maintenance: "None",
    best: "Defense-in-depth (Chrome/Edge)"
  },
  {
    method: "Permissions Policy",
    protects: "Feature abuse",
    server: "No",
    maintenance: "None",
    best: "All sites (good hygiene)"
  },
];
